/**
 * @description - Gets the keys of an enum
 * @param {object} enumObject - The enum object
 * @returns {number[]} - An array of the enum keys
 * @example - enumKeys(MyEnum) // [0, 1, 2]
 */
export function enumKeys(enumObject: object): string[] {
  return Object.keys(enumObject).filter((k) => Number.isNaN(Number(k)));
}

/**
  * @description - Helper to get only the string keys of an enum
  * Useful since typescript enums have both string and numeric keys
  * See: https://www.typescriptlang.org/docs/handbook/enums.html#reverse-mappings
  */
export function enumStringKeys<T extends object>(e: T): Array<Extract<keyof T, string>> {
  return Object.keys(e).filter((k) => Number.isNaN(Number(k))) as Array<Extract<keyof T, string>>;
}

/**
 * @description - Gets the key of an enum by its value
 * @param {object} enumObject - The enum object
 * @param {unknown} value - The value to find the key for
 * @returns {string} - The key of the enum or an empty string if not found
 */
export function enumKeyOf<T extends EnumLike>(enumObject: T, value: unknown): string {
  if (value == null) return '';

  // Numeric enums (reverse mapping exists: 1 -> "Foo")
  if (typeof value === 'number') {
    const maybe = (enumObject as { [k: number]: string | number })[value];
    return typeof maybe === 'string' ? maybe : '';
  }

  // String enums (no reverse map): the value is a string
  if (typeof value === 'string') {
    // If it's already a key, return it
    if (Object.hasOwn(enumObject, value)) return value;

    // Otherwise, try to find the key whose value matches this string
    for (const [k, v] of Object.entries(enumObject)) {
      if (v === value) return k;
    }

    // Last resort: treat as-is
    return value;
  }

  // Anything else (objects, booleans, symbols, etc.)
  return '';
}

/**
 * @description - Parses a string key into its corresponding enum value
 * @param {object} enumObject - The enum object
 * @param {string} key - The key to parse
 * @returns {unknown} - The enum value or undefined if not found
 */
export function enumParse<T extends EnumLike>(enumObject: T, key: string): unknown {
  return enumObject[key as keyof T];
}
